import React, { useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useForm, useController, useFieldArray } from 'react-hook-form';
import Swal from 'sweetalert2';
import useFormDraft from '../../hooks/useFormDraft';
import LoadDraftButton from '../LoadDraftButton';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Divider,
  Paper,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import BloodtypeIcon from '@mui/icons-material/Bloodtype';
import ScienceIcon from '@mui/icons-material/Science';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import MDBox from 'components/MDBox';
import MDDatePicker from 'components/MDDatePicker';
import LoaderOverlay from 'components/LoaderOverlay';
import RichTextEditor from 'components/RichTextEditor';
import { formatDateDDMMYYYY } from 'utils/dateUtils';
import { api } from '../../api';

// Blood group options for ABORh test
const BLOOD_GROUP_OPTIONS = [
  { label: 'A Positive (A+)', value: 'A+' },
  { label: 'A Negative (A-)', value: 'A-' },
  { label: 'B Positive (B+)', value: 'B+' },
  { label: 'B Negative (B-)', value: 'B-' },
  { label: 'AB Positive (AB+)', value: 'AB+' },
  { label: 'AB Negative (AB-)', value: 'AB-' },
  { label: 'O Positive (O+)', value: 'O+' },
  { label: 'O Negative (O-)', value: 'O-' },
  { label: 'Bombay Blood Group (Oh)', value: 'Oh' },
];

// Helper function to get full blood group label from value
const getBloodGroupLabel = (value) => {
  const option = BLOOD_GROUP_OPTIONS.find(opt => opt.value === value);
  return option ? option.label : value;
};

// Controlled Select Component using useController
const ControlledSelect = React.memo(
  ({ name, label, options, index, onSelectionChange, control, selectBoxHeight }) => {
    const {
      field: { value, onChange },
      fieldState: { error },
    } = useController({
      name,
      control,
      rules: { required: 'Test selection is required' },
    });

    return (
      <FormControl fullWidth size="small" error={!!error}>
        <InputLabel>{label}</InputLabel>
        <Select
          value={value || ''}
          label={label}
          onChange={(e) => {
            const selectedValue = e.target.value;
            const selectedOption = options.find(
              (option) => String(option.id) === String(selectedValue),
            );
            onChange(selectedValue);
            // Also update test_name field
            if (onSelectionChange) {
              onSelectionChange(selectedOption?.option_value || '');
            }
          }}
          sx={{
            height: selectBoxHeight,
            '& .MuiOutlinedInput-root': {
              '&.Mui-focused fieldset': {
                borderColor: '#00798E',
              },
            },
          }}
        >
          {options.length === 0 ? (
            <MenuItem disabled value="">
              No options available
            </MenuItem>
          ) : (
            options.map((option) => (
              <MenuItem key={option.id} value={String(option.id)}>
                {option.option_value}
              </MenuItem>
            ))
          )}
        </Select>
        {error && (
          <Typography variant="caption" color="error" sx={{ ml: 1, mt: 0.5 }}>
            {error.message}
          </Typography>
        )}
      </FormControl>
    );
  },
);

// PropTypes for ControlledSelect
ControlledSelect.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      option_value: PropTypes.string,
    }),
  ).isRequired,
  index: PropTypes.number,
  onSelectionChange: PropTypes.func,
  control: PropTypes.object.isRequired,
  selectBoxHeight: PropTypes.number.isRequired,
};

// Controlled TextField Component
const ControlledTextField = React.memo(
  ({ name, label, type = 'text', control, selectBoxHeight, inputProps: callerInputProps, ...props }) => {
    const {
      field: { value, onChange },
      fieldState: { error },
    } = useController({
      name,
      control,
      rules: props.required ? { required: `${label} is required` } : {},
    });

    const handleChange = (event) => {
      const inputValue = event.target.value;
      // Convert to uppercase for text inputs (but not for date/number inputs)
      const finalValue = type === 'text' ? inputValue.toUpperCase() : inputValue;
      onChange(finalValue);
    };

    return (
      <TextField
        fullWidth
        label={label}
        type={type}
        value={value || ''}
        onChange={handleChange}
        error={!!error}
        helperText={error?.message}
        size="small"
        inputProps={{ autoComplete: 'off', ...callerInputProps }}
        sx={{
          '& .MuiOutlinedInput-root': {
            height: type === 'date' ? selectBoxHeight : 'auto',
            minHeight: selectBoxHeight,
            '&.Mui-focused fieldset': {
              borderColor: '#00798E',
            },
          },
          '& .MuiInputBase-input': { 
            textTransform: 'uppercase' 
          },
        }}
        {...props}
      />
    );
  },
);

// PropTypes for ControlledTextField
ControlledTextField.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  type: PropTypes.string,
  required: PropTypes.bool,
  control: PropTypes.object.isRequired,
  selectBoxHeight: PropTypes.number.isRequired,
  inputProps: PropTypes.object,
};

function MaleInvestigation({
  patientData,
  investigationParams,
  onSaveInvestigation,
  onFetchContactHistory,
  onDeleteHistory,
  saving,
}) {
  const [expanded, setExpanded] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const [existingHistory, setExistingHistory] = useState({
    hormonal: [],
    serology: [],
    biochemistry: [],
    semenAnalysis: [],
    otherInvestigation: [],
  });
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [savingHormonal, setSavingHormonal] = useState(false);
  const [savingSerology, setSavingSerology] = useState(false);
  const [savingBiochemistry, setSavingBiochemistry] = useState(false);
  const [savingSemenAnalysis, setSavingSemenAnalysis] = useState(false);
  const [savingOtherInvestigation, setSavingOtherInvestigation] = useState(false);
  const [deletingHistory, setDeletingHistory] = useState(false);
  const [editingHistory, setEditingHistory] = useState({});
  const [updatingHistory, setUpdatingHistory] = useState(false);

  // Draft management for each investigation type
  const hormonalDraft = useFormDraft(`male_investigation_hormonal_${patientData?.id}`, 1500);
  const serologyDraft = useFormDraft(`male_investigation_serology_${patientData?.id}`, 1500);
  const biochemistryDraft = useFormDraft(`male_investigation_biochemistry_${patientData?.id}`, 1500);
  const semenAnalysisDraft = useFormDraft(`male_investigation_semen_${patientData?.id}`, 1500);
  const otherInvestigationDraft = useFormDraft(`male_investigation_other_${patientData?.id}`, 1500);

  // Initialize React Hook Form
  const {
    control,
    handleSubmit,
    watch,
    reset,
    getValues,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      hormonalForms: [],
      serologyForms: [],
      biochemistryForms: [],
      semenAnalysisForms: [],
      otherInvestigationForms: [],
    },
  });

  // Field arrays for dynamic forms
  const {
    fields: hormonalFields,
    append: appendHormonal,
    remove: removeHormonal,
  } = useFieldArray({
    control,
    name: 'hormonalForms',
  });

  const {
    fields: serologyFields,
    append: appendSerology,
    remove: removeSerology,
  } = useFieldArray({
    control,
    name: 'serologyForms',
  });

  const {
    fields: biochemistryFields,
    append: appendBiochemistry,
    remove: removeBiochemistry,
  } = useFieldArray({
    control,
    name: 'biochemistryForms',
  });

  const {
    fields: semenAnalysisFields,
    append: appendSemenAnalysis,
    remove: removeSemenAnalysis,
    update: updateSemenAnalysis,
  } = useFieldArray({
    control,
    name: 'semenAnalysisForms',
  });

  const {
    fields: otherInvestigationFields,
    append: appendOtherInvestigation,
    remove: removeOtherInvestigation,
  } = useFieldArray({
    control,
    name: 'otherInvestigationForms',
  });

  // Watch form values for real-time updates
  const watchedHormonalForms = watch('hormonalForms');
  const watchedSerologyForms = watch('serologyForms');
  const watchedBiochemistryForms = watch('biochemistryForms');
  const watchedSemenAnalysisForms = watch('semenAnalysisForms');
  const watchedOtherInvestigationForms = watch('otherInvestigationForms');

  // Set the height for the select box (consistent with patientRegistration)
  const selectBoxHeight = 45;

  // Extract dynamic options from investigationParams for blood investigation
  const getBloodOptionsFromParams = (slug) => {
    if (!investigationParams?.blood_investigation_params?.param_group_members) {
      return [];
    }

    const member = investigationParams.blood_investigation_params.param_group_members.find(
      (member) => member.slug === slug,
    );

    return member?.param_group_member_options || [];
  };

  // Extract dynamic options from investigationParams for male investigation
  const getMaleOptionsFromParams = (slug) => {
    if (!investigationParams?.male_investigation_params?.param_group_members) {
      return [];
    }

    const member = investigationParams.male_investigation_params.param_group_members.find(
      (member) => member.slug === slug,
    );

    return member?.param_group_member_options || [];
  };

  // Dynamic master data for dropdown options
  const hormonalOptions = getBloodOptionsFromParams('blood-investigation-params-hormonal');
  const serologyOptions = getBloodOptionsFromParams('blood-investigation-params-serology');
  const biochemistryOptions = getBloodOptionsFromParams('blood-investigation-params-bio-chemistry');
  const semenAnalysisOptions = getMaleOptionsFromParams('male-investigation-params-semen-analysis');
  const otherInvestigationOptions = getMaleOptionsFromParams('male-investigation-params-radiology');

  // Fetch existing contact history on component mount
  const fetchContactHistory = async () => {
    if (!patientData?.id || !onFetchContactHistory) return;

    setLoadingHistory(true);
    try {
      const response = await onFetchContactHistory(patientData.id);
      if (response?.contact_histories) {
        const bloodInvestigationGroupId = investigationParams?.blood_investigation_params?.id;
        const maleInvestigationGroupId = investigationParams?.male_investigation_params?.id;

        // Filter histories for blood investigation parameters
        const bloodHistories = response.contact_histories.filter(
          (history) => history.irm_param_groups_id === bloodInvestigationGroupId,
        );

        // Filter histories for male investigation parameters
        const maleHistories = response.contact_histories.filter(
          (history) => history.irm_param_groups_id === maleInvestigationGroupId,
        );

        console.log("bloodHistories:", bloodHistories);
        console.log("maleHistories:", maleHistories);

        // Organize by param group member slug
        const organizedHistory = {
          hormonal: [],
          serology: [],
          biochemistry: [],
          semenAnalysis: [],
          otherInvestigation: [],
        };

        // Process blood investigation histories
        bloodHistories.forEach((history) => {
          const memberSlug = history.param_group_member?.slug;
          if (memberSlug === 'blood-investigation-params-hormonal') {
            organizedHistory.hormonal.push({
              id: history.id,
              name: history.param_group_member_option?.value || history.name,
              value: history.value,
              history_date: new Date(history.history_date).toISOString().split('T')[0],
              remarks: history.remarks || '',
              // Keep original data for deletion and editing
              contact_id: history.irm_contacts_id,
              param_group_id: history.irm_param_groups_id,
              param_group_member_id: history.irm_param_group_members_id,
              param_group_member_option_id: history.irm_param_group_member_options_id,
            });
          } else if (memberSlug === 'blood-investigation-params-serology') {
            organizedHistory.serology.push({
              id: history.id,
              name: history.param_group_member_option?.value || history.name,
              value: history.value,
              history_date: new Date(history.history_date).toISOString().split('T')[0],
              remarks: history.remarks || '',
              // Keep original data for deletion and editing
              contact_id: history.irm_contacts_id,
              param_group_id: history.irm_param_groups_id,
              param_group_member_id: history.irm_param_group_members_id,
              param_group_member_option_id: history.irm_param_group_member_options_id,
            });
          } else if (memberSlug === 'blood-investigation-params-bio-chemistry') {
            organizedHistory.biochemistry.push({
              id: history.id,
              name: history.param_group_member_option?.value || history.name,
              value: history.value,
              history_date: new Date(history.history_date).toISOString().split('T')[0],
              remarks: history.remarks || '',
              // Keep original data for deletion and editing
              contact_id: history.irm_contacts_id,
              param_group_id: history.irm_param_groups_id,
              param_group_member_id: history.irm_param_group_members_id,
              param_group_member_option_id: history.irm_param_group_member_options_id,
            });
          }
        });

        // Process male investigation histories
        maleHistories.forEach((history) => {
          const memberSlug = history.param_group_member?.slug;
          if (memberSlug === 'male-investigation-params-semen-analysis') {
            organizedHistory.semenAnalysis.push({
              id: history.id,
              name: history.param_group_member_option?.value || history.name,
              value: history.value,
              history_date: new Date(history.history_date).toISOString().split('T')[0],
              remarks: history.remarks || '',
              // Keep original data for deletion and editing
              contact_id: history.irm_contacts_id,
              param_group_id: history.irm_param_groups_id,
              param_group_member_id: history.irm_param_group_members_id,
              param_group_member_option_id: history.irm_param_group_member_options_id,
            });
          } else if (memberSlug === 'male-investigation-params-radiology') {
            organizedHistory.otherInvestigation.push({
              id: history.id,
              name: history.param_group_member_option?.value || history.name,
              value: history.value,
              history_date: new Date(history.history_date).toISOString().split('T')[0],
              remarks: history.remarks || '',
              // Keep original data for deletion and editing
              contact_id: history.irm_contacts_id,
              param_group_id: history.irm_param_groups_id,
              param_group_member_id: history.irm_param_group_members_id,
              param_group_member_option_id: history.irm_param_group_member_options_id,
            });
          }
        });

        setExistingHistory(organizedHistory);
      }
    } catch (error) {
      console.error('Error fetching contact history:', error);
      setSaveMessage('Error fetching existing history');
      setTimeout(() => setSaveMessage(''), 3000);
    } finally {
      setLoadingHistory(false);
    }
  };

  // Handle delete history
  const handleDeleteHistory = async (historyItem, section) => {
    if (!onDeleteHistory) return;

    // Show confirmation dialog before deletion
    const result = await Swal.fire({
      title: 'Delete Investigation Record?',
      text: `Are you sure you want to delete "${historyItem.name}" record? This action cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Yes, Delete',
      cancelButtonText: 'Cancel',
      reverseButtons: true,
    });

    // If user cancelled, don't proceed with deletion
    if (!result.isConfirmed) {
      return;
    }

    setDeletingHistory(true);
    try {
      await onDeleteHistory(historyItem);

      // Remove from local state
      setExistingHistory((prev) => ({
        ...prev,
        [section]: prev[section].filter((item) => item.id !== historyItem.id),
      }));

      // Show success message
      await Swal.fire({
        title: 'Deleted!',
        text: 'Investigation record has been deleted successfully.',
        icon: 'success',
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (error) {
      console.error('Error deleting history:', error);
      
      // Show error message
      await Swal.fire({
        title: 'Error!',
        text: 'Failed to delete investigation record. Please try again.',
        icon: 'error',
        confirmButtonColor: '#dc3545',
      });
    } finally {
      setDeletingHistory(false);
    }
  };

  // Handle edit history - Toggle edit mode
  const handleEditHistory = (historyItem, section) => {
    const editKey = `${section}_${historyItem.id}`;
    setEditingHistory((prev) => ({
      ...prev,
      [editKey]: {
        ...historyItem,
        isEditing: true,
      },
    }));
  };

  // Handle cancel edit
  const handleCancelEdit = (historyItem, section) => {
    const editKey = `${section}_${historyItem.id}`;
    setEditingHistory((prev) => {
      const updated = { ...prev };
      delete updated[editKey];
      return updated;
    });
  };

  // Handle save edited history
  const handleSaveEdit = async (historyItem, section) => {
    const editKey = `${section}_${historyItem.id}`;
    const editedData = editingHistory[editKey];

    if (!editedData) return;

    // Validate required fields
    if (!editedData.value && section !== 'semenAnalysis' && section !== 'otherInvestigation') {
      Swal.fire({
        icon: 'warning',
        title: 'Missing Value',
        text: 'Please enter a value for this test.',
        confirmButtonColor: '#00798E',
      });
      return;
    }

    // History date is now optional - removed validation

    setUpdatingHistory(true);
    try {
      // Prepare update data
      const updateData = {
        contact_id: editedData.contact_id,
        param_group_id: editedData.param_group_id,
        param_group_member_id: editedData.param_group_member_id,
        param_group_member_option_id: editedData.param_group_member_option_id,
        contact_history_id: editedData.id,
        value: editedData.value,
        history_date: editedData.history_date,
        remarks: editedData.remarks || null,
      };

      // Call update API
      await api.opd.updateContactHistory(updateData);

      // Update local state
      setExistingHistory((prev) => ({
        ...prev,
        [section]: prev[section].map((item) =>
          item.id === editedData.id
            ? { 
                ...item, 
                name: editedData.name,
                param_group_member_option_id: editedData.param_group_member_option_id,
                value: editedData.value, 
                history_date: editedData.history_date, 
                remarks: editedData.remarks 
              }
            : item
        ),
      }));

      // Clear editing state
      handleCancelEdit(historyItem, section);

      // Show success message
      Swal.fire({
        icon: 'success',
        title: 'Updated!',
        text: 'Investigation record has been updated successfully.',
        confirmButtonColor: '#00798E',
        timer: 2000,
        showConfirmButton: true,
      });
    } catch (error) {
      console.error('Error updating history:', error);
      Swal.fire({
        icon: 'error',
        title: 'Update Failed',
        text: error.message || 'Failed to update investigation record. Please try again.',
        confirmButtonColor: '#00798E',
      });
    } finally {
      setUpdatingHistory(false);
    }
  };

  // Handle field change in edit mode
  const handleEditFieldChange = (historyItem, section, field, value) => {
    const editKey = `${section}_${historyItem.id}`;
    setEditingHistory((prev) => ({
      ...prev,
      [editKey]: {
        ...prev[editKey],
        [field]: value,
      },
    }));
  };

  // Fetch history when component mounts or patient changes
  React.useEffect(() => {
    if (patientData?.id && (investigationParams?.blood_investigation_params?.id || investigationParams?.male_investigation_params?.id)) {
      fetchContactHistory();
    }
  }, [patientData?.id, investigationParams?.blood_investigation_params?.id, investigationParams?.male_investigation_params?.id]);

  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  // Add new form functions using field array
  const addHormonalForm = () => {
    appendHormonal({
      test_id: '', // Store option ID as string
      test_name: '', // Store option name for display
      date: '',
      value: '',
      remarks: '',
    });
  };

  const addSerologyForm = () => {
    appendSerology({
      test_id: '', // Store option ID as string
      test_name: '', // Store option name for display
      date: '',
      value: '',
      remarks: '',
    });
  };

  const addBiochemistryForm = () => {
    appendBiochemistry({
      test_id: '', // Store option ID as string
      test_name: '', // Store option name for display
      date: '',
      value: '',
      remarks: '',
    });
  };

  const addSemenAnalysisForm = () => {
    // Check if there are existing forms and get the most recent date
    const existingForms = watchedSemenAnalysisForms;
    const latestDate = existingForms.length > 0 ? 
      existingForms[existingForms.length - 1].date : '';

    appendSemenAnalysis({
      test_id: '', // Store option ID as string
      test_name: '', // Store option name for display
      date: latestDate, // Copy date from the most recent form if available
      value: '',
      remarks: '',
    });
  };

  const addOtherInvestigationForm = () => {
    appendOtherInvestigation({
      // Remove test_id field for other investigation since it's free text
      test_name: '', // Store test name directly for other investigation
      date: '',
      // Remove value field for other investigation
      remarks: '',
    });
  };

  // Auto-save draft data when form fields change
  useEffect(() => {
    if (watchedHormonalForms?.length > 0) {
      hormonalDraft.autoSave({ forms: watchedHormonalForms });
    }
  }, [watchedHormonalForms]);

  useEffect(() => {
    if (watchedSerologyForms?.length > 0) {
      serologyDraft.autoSave({ forms: watchedSerologyForms });
    }
  }, [watchedSerologyForms]);

  useEffect(() => {
    if (watchedBiochemistryForms?.length > 0) {
      biochemistryDraft.autoSave({ forms: watchedBiochemistryForms });
    }
  }, [watchedBiochemistryForms]);

  useEffect(() => {
    if (watchedSemenAnalysisForms?.length > 0) {
      semenAnalysisDraft.autoSave({ forms: watchedSemenAnalysisForms });
    }
  }, [watchedSemenAnalysisForms]);

  useEffect(() => {
    if (watchedOtherInvestigationForms?.length > 0) {
      otherInvestigationDraft.autoSave({ forms: watchedOtherInvestigationForms });
    }
  }, [watchedOtherInvestigationForms]);

  // Draft load handlers
  const handleLoadHormonalDraft = async () => {
    const data = await hormonalDraft.loadDraftData();
    if (data?.forms) {
      reset({
        ...getValues(),
        hormonalForms: data.forms,
      });
    }
  };

  const handleLoadSerologyDraft = async () => {
    const data = await serologyDraft.loadDraftData();
    if (data?.forms) {
      reset({
        ...getValues(),
        serologyForms: data.forms,
      });
    }
  };

  const handleLoadBiochemistryDraft = async () => {
    const data = await biochemistryDraft.loadDraftData();
    if (data?.forms) {
      reset({
        ...getValues(),
        biochemistryForms: data.forms,
      });
    }
  };

  const handleLoadSemenAnalysisDraft = async () => {
    const data = await semenAnalysisDraft.loadDraftData();
    if (data?.forms) {
      reset({
        ...getValues(),
        semenAnalysisForms: data.forms,
      });
    }
  };

  const handleLoadOtherInvestigationDraft = async () => {
    const data = await otherInvestigationDraft.loadDraftData();
    if (data?.forms) {
      reset({
        ...getValues(),
        otherInvestigationForms: data.forms,
      });
    }
  };

  // Save functions using watched form data with proper API format
  const saveHormonalData = async () => {
    setSavingHormonal(true);
    try {
      const validForms = watchedHormonalForms.filter(
        (form) => form.test_id && form.date && form.value,
      );
      if (validForms.length === 0) {
        setSaveMessage('Please add at least one complete test entry');
        setTimeout(() => setSaveMessage(''), 3000);
        setSavingHormonal(false);
        return;
      }

      // Find the hormonal param group member
      const hormonalParamMember =
        investigationParams?.blood_investigation_params?.param_group_members?.find(
          (member) => member.slug === 'blood-investigation-params-hormonal',
        );

      // Format data according to API requirements
      const formattedData = validForms.map((form) => ({
        contact_id: patientData?.id,
        param_group_id: investigationParams?.blood_investigation_params?.id,
        param_group_member_id: hormonalParamMember?.id,
        param_group_member_option_id: parseInt(form.test_id),
        name: form.test_name,
        value: form.value,
        history_date: form.date,
        remarks: form.remarks || null,
        private_note: null,
      }));

      // Use handleSubmitWithDraft - saves to IndexedDB first, then API
      await hormonalDraft.handleSubmitWithDraft(
        async (data) => {
          await onSaveInvestigation(data);
          return { success: true };
        },
        formattedData
      );
      
      setSaveMessage('Hormonal data saved successfully!');
      setTimeout(() => setSaveMessage(''), 3000);

      // Refresh history after successful save
      await fetchContactHistory();

      // Clear the form
      reset({
        hormonalForms: [],
        serologyForms: watchedSerologyForms,
        biochemistryForms: watchedBiochemistryForms,
        semenAnalysisForms: watchedSemenAnalysisForms,
        otherInvestigationForms: watchedOtherInvestigationForms,
      });
    } catch (error) {
      console.error('Error saving hormonal data:', error);
      setSaveMessage('Error saving hormonal data');
      setTimeout(() => setSaveMessage(''), 3000);
    } finally {
      setSavingHormonal(false);
    }
  };

  const saveSerologyData = async () => {
    setSavingSerology(true);
    try {
      const validForms = watchedSerologyForms.filter(
        (form) => form.test_id && form.date && form.value,
      );
      if (validForms.length === 0) {
        setSaveMessage('Please add at least one complete test entry');
        setTimeout(() => setSaveMessage(''), 3000);
        setSavingSerology(false);
        return;
      }

      // Find the serology param group member
      const serologyParamMember =
        investigationParams?.blood_investigation_params?.param_group_members?.find(
          (member) => member.slug === 'blood-investigation-params-serology',
        );

      // Format data according to API requirements
      const formattedData = validForms.map((form) => ({
        contact_id: patientData?.id,
        param_group_id: investigationParams?.blood_investigation_params?.id,
        param_group_member_id: serologyParamMember?.id,
        param_group_member_option_id: parseInt(form.test_id),
        name: form.test_name,
        value: form.value,
        history_date: form.date,
        remarks: form.remarks || null,
        private_note: null,
      }));

      // Use handleSubmitWithDraft - saves to IndexedDB first, then API
      await serologyDraft.handleSubmitWithDraft(
        async (data) => {
          await onSaveInvestigation(data);
          return { success: true };
        },
        formattedData
      );
      
      setSaveMessage('Serology data saved successfully!');
      setTimeout(() => setSaveMessage(''), 3000);

      // Refresh history after successful save
      await fetchContactHistory();

      // Clear the form
      reset({
        hormonalForms: watchedHormonalForms,
        serologyForms: [],
        biochemistryForms: watchedBiochemistryForms,
        semenAnalysisForms: watchedSemenAnalysisForms,
        otherInvestigationForms: watchedOtherInvestigationForms,
      });
    } catch (error) {
      console.error('Error saving serology data:', error);
      setSaveMessage('Error saving serology data');
      setTimeout(() => setSaveMessage(''), 3000);
    } finally {
      setSavingSerology(false);
    }
  };

  const saveBiochemistryData = async () => {
    setSavingBiochemistry(true);
    try {
      const validForms = watchedBiochemistryForms.filter(
        (form) => form.test_id && form.date && form.value,
      );
      if (validForms.length === 0) {
        setSaveMessage('Please add at least one complete test entry');
        setTimeout(() => setSaveMessage(''), 3000);
        setSavingBiochemistry(false);
        return;
      }

      // Find the biochemistry param group member
      const biochemistryParamMember =
        investigationParams?.blood_investigation_params?.param_group_members?.find(
          (member) => member.slug === 'blood-investigation-params-bio-chemistry',
        );

      // Format data according to API requirements
      const formattedData = validForms.map((form) => ({
        contact_id: patientData?.id,
        param_group_id: investigationParams?.blood_investigation_params?.id,
        param_group_member_id: biochemistryParamMember?.id,
        param_group_member_option_id: parseInt(form.test_id),
        name: form.test_name,
        value: form.value,
        history_date: form.date,
        remarks: form.remarks || null,
        private_note: null,
      }));

      // Use handleSubmitWithDraft - saves to IndexedDB first, then API
      await biochemistryDraft.handleSubmitWithDraft(
        async (data) => {
          await onSaveInvestigation(data);
          return { success: true };
        },
        formattedData
      );
      
      setSaveMessage('Biochemistry data saved successfully!');
      setTimeout(() => setSaveMessage(''), 3000);

      // Refresh history after successful save
      await fetchContactHistory();

      // Clear the form
      reset({
        hormonalForms: watchedHormonalForms,
        serologyForms: watchedSerologyForms,
        biochemistryForms: [],
        semenAnalysisForms: watchedSemenAnalysisForms,
        otherInvestigationForms: watchedOtherInvestigationForms,
      });
    } catch (error) {
      console.error('Error saving biochemistry data:', error);
      setSaveMessage('Error saving biochemistry data');
      setTimeout(() => setSaveMessage(''), 3000);
    } finally {
      setSavingBiochemistry(false);
    }
  };

  const saveSemenAnalysisData = async () => {
    setSavingSemenAnalysis(true);
    try {
      const validForms = watchedSemenAnalysisForms.filter(
        (form) => form.test_id && form.date && form.value,
      );
      if (validForms.length === 0) {
        setSaveMessage('Please add at least one complete test entry');
        setTimeout(() => setSaveMessage(''), 3000);
        setSavingSemenAnalysis(false);
        return;
      }

      // Find the semen analysis param group member
      const semenAnalysisParamMember =
        investigationParams?.male_investigation_params?.param_group_members?.find(
          (member) => member.slug === 'male-investigation-params-semen-analysis',
        );

      // Format data according to API requirements
      const formattedData = validForms.map((form) => ({
        contact_id: patientData?.id,
        param_group_id: investigationParams?.male_investigation_params?.id,
        param_group_member_id: semenAnalysisParamMember?.id,
        param_group_member_option_id: parseInt(form.test_id),
        name: form.test_name,
        value: form.value,
        history_date: form.date,
        remarks: form.remarks || null,
        private_note: null,
      }));

      // Use handleSubmitWithDraft - saves to IndexedDB first, then API
      await semenAnalysisDraft.handleSubmitWithDraft(
        async (data) => {
          await onSaveInvestigation(data);
          return { success: true };
        },
        formattedData
      );
      
      setSaveMessage('Semen Analysis data saved successfully!');
      setTimeout(() => setSaveMessage(''), 3000);

      // Refresh history after successful save
      await fetchContactHistory();

      // Clear the form
      reset({
        hormonalForms: watchedHormonalForms,
        serologyForms: watchedSerologyForms,
        biochemistryForms: watchedBiochemistryForms,
        semenAnalysisForms: [],
        otherInvestigationForms: watchedOtherInvestigationForms,
      });
    } catch (error) {
      console.error('Error saving semen analysis data:', error);
      setSaveMessage('Error saving semen analysis data');
      setTimeout(() => setSaveMessage(''), 3000);
    } finally {
      setSavingSemenAnalysis(false);
    }
  };

  const saveOtherInvestigationData = async () => {
    setSavingOtherInvestigation(true);
    try {
      const validForms = watchedOtherInvestigationForms.filter(
        (form) => form.test_name && form.date, // Use test_name instead of test_id for other investigation
      );
      if (validForms.length === 0) {
        setSaveMessage('Please add at least one complete test entry');
        setTimeout(() => setSaveMessage(''), 3000);
        setSavingOtherInvestigation(false);
        return;
      }

      // Find the radiology param group member
      const radiologyParamMember =
        investigationParams?.male_investigation_params?.param_group_members?.find(
          (member) => member.slug === 'male-investigation-params-radiology',
        );

      // Format data according to API requirements
      const formattedData = validForms.map((form) => ({
        contact_id: patientData?.id,
        param_group_id: investigationParams?.male_investigation_params?.id,
        param_group_member_id: radiologyParamMember?.id,
        param_group_member_option_id: null, // Set as null for free text other investigation
        name: form.test_name,
        value: null, // Set value as null for other investigation
        history_date: form.date,
        remarks: form.remarks || null,
        private_note: null,
      }));

      // Use handleSubmitWithDraft - saves to IndexedDB first, then API
      await otherInvestigationDraft.handleSubmitWithDraft(
        async (data) => {
          await onSaveInvestigation(data);
          return { success: true };
        },
        formattedData
      );
      
      setSaveMessage('Other Investigation data saved successfully!');
      setTimeout(() => setSaveMessage(''), 3000);

      // Refresh history after successful save
      await fetchContactHistory();

      // Clear the form
      reset({
        hormonalForms: watchedHormonalForms,
        serologyForms: watchedSerologyForms,
        biochemistryForms: watchedBiochemistryForms,
        semenAnalysisForms: watchedSemenAnalysisForms,
        otherInvestigationForms: [],
      });
    } catch (error) {
      console.error('Error saving other investigation data:', error);
      setSaveMessage('Error saving other investigation data');
      setTimeout(() => setSaveMessage(''), 3000);
    } finally {
      setSavingOtherInvestigation(false);
    }
  };

  // Render existing history table
  const renderHistoryTable = useCallback(
    (historyData, section, title) => {
      if (!historyData || historyData.length === 0) {
        return (
          <Paper sx={{ p: 2, mb: 2, backgroundColor: '#f8f9fa' }}>
            <Typography variant="body2" color="text.secondary" textAlign="center">
              No existing {title.toLowerCase()} history found
            </Typography>
          </Paper>
        );
      }

      // Get test options based on section
      const getTestOptions = () => {
        switch (section) {
          case 'hormonal':
            return hormonalOptions;
          case 'serology':
            return serologyOptions;
          case 'biochemistry':
            return biochemistryOptions;
          case 'semenAnalysis':
            return semenAnalysisOptions;
          case 'otherInvestigation':
            return otherInvestigationOptions;
          default:
            return [];
        }
      };

      const testOptions = getTestOptions();

      return (
        <TableContainer component={Paper} sx={{ mb: 2 }}>
          <Table size="small">
            <TableBody>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 'bold' }}>Test Name</TableCell>
                {/* Conditionally render Value column - exclude for Other Investigation */}
                {section !== 'otherInvestigation' && (
                  <TableCell sx={{ fontWeight: 'bold' }}>Value</TableCell>
                )}
                <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Remarks</TableCell>
                <TableCell sx={{ fontWeight: 'bold', width: '100px', textAlign: 'center' }}>Actions</TableCell>
              </TableRow>
              {historyData.map((history) => {
                const editKey = `${section}_${history.id}`;
                const isEditing = editingHistory[editKey]?.isEditing;
                const editedData = editingHistory[editKey] || history;

                return (
                  <TableRow key={history.id} hover>
                    <TableCell>
                      {isEditing ? (
                        section === 'otherInvestigation' ? (
                          <TextField
                            fullWidth
                            size="small"
                            value={editedData.name || ''}
                            onChange={(e) => handleEditFieldChange(history, section, 'name', e.target.value)}
                            placeholder="Enter test name"
                            sx={{ 
                              minWidth: 200,
                              '& .MuiOutlinedInput-root': {
                                '&.Mui-focused fieldset': {
                                  borderColor: '#00798E',
                                },
                              },
                            }}
                          />
                        ) : (
                          <FormControl fullWidth size="small">
                            <InputLabel>Test</InputLabel>
                            <Select
                              value={editedData.param_group_member_option_id || ''}
                              label="Test"
                              onChange={(e) => {
                                const selectedOptionId = e.target.value;
                                const selectedOption = testOptions.find(opt => opt.id === selectedOptionId);
                                handleEditFieldChange(history, section, 'param_group_member_option_id', selectedOptionId);
                                // Also update the test name for display
                                if (selectedOption) {
                                  handleEditFieldChange(history, section, 'name', selectedOption.option_value);
                                }
                              }}
                              sx={{ 
                                minWidth: 200,
                                height: 40,
                                '& .MuiOutlinedInput-root': {
                                  '&.Mui-focused fieldset': {
                                    borderColor: '#00798E',
                                  },
                                },
                              }}
                            >
                              {testOptions.map((option) => (
                                <MenuItem key={option.id} value={option.id}>
                                  {option.option_value}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        )
                      ) : (
                        history.name
                      )}
                    </TableCell>
                    {/* Conditionally render Value cell - exclude for Other Investigation */}
                    {section !== 'otherInvestigation' && (
                      <TableCell>
                        {isEditing ? (
                          section === 'biochemistry' && history.name?.toLowerCase().includes('aborh') ? (
                            <FormControl fullWidth size="small">
                              <Select
                                value={editedData.value || ''}
                                onChange={(e) => handleEditFieldChange(history, section, 'value', e.target.value)}
                                sx={{ minWidth: 150 }}
                              >
                                {BLOOD_GROUP_OPTIONS.map((option) => (
                                  <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          ) : (
                            <RichTextEditor
                              value={editedData.value || ''}
                              onChange={(value) => handleEditFieldChange(history, section, 'value', value)}
                              height="60px"
                              placeholder="Enter result value"
                            />
                          )
                        ) : (
                          <>
                            {/* Show full blood group label for ABORh tests */}
                            {section === 'biochemistry' && history.name?.toLowerCase().includes('aborh') ? (
                              getBloodGroupLabel(history.value)
                            ) : (
                              <Box
                                dangerouslySetInnerHTML={{ __html: history.value || 'N/A' }}
                                sx={{
                                  maxWidth: 200,
                                  overflow: 'auto',
                                  '& p, & ul, & ol': {
                                    margin: 0,
                                  },
                                }}
                              />
                            )}
                          </>
                        )}
                      </TableCell>
                    )}
                    <TableCell>
                      {isEditing ? (
                        <MDDatePicker
                          label="Date"
                          value={editedData.history_date || ''}
                          onChange={(date) => handleEditFieldChange(history, section, 'history_date', date)}
                          textFieldProps={{
                            fullWidth: true,
                            size: 'small',
                            sx: {
                              '& .MuiOutlinedInput-root': {
                                height: 40,
                                '&.Mui-focused fieldset': {
                                  borderColor: '#00798E',
                                },
                              },
                            },
                          }}
                        />
                      ) : (
                        history.history_date ? formatDateDDMMYYYY(history.history_date) : '-'
                      )}
                    </TableCell>
                    <TableCell>
                      {isEditing ? (
                        <RichTextEditor
                          value={editedData.remarks || ''}
                          onChange={(value) => handleEditFieldChange(history, section, 'remarks', value)}
                          height="80px"
                          placeholder="Enter remarks"
                        />
                      ) : (
                        <Box
                          dangerouslySetInnerHTML={{ __html: history.remarks || 'N/A' }}
                          sx={{
                            maxWidth: 200,
                            overflow: 'auto',
                            '& p, & ul, & ol': {
                              margin: 0,
                            },
                          }}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      {isEditing ? (
                        <Box display="flex" gap={0.5} justifyContent="center">
                          <IconButton
                            size="small"
                            onClick={() => handleSaveEdit(history, section)}
                            disabled={updatingHistory}
                            sx={{
                              color: '#28a745',
                              '&:hover': {
                                backgroundColor: 'rgba(40, 167, 69, 0.1)',
                              },
                            }}
                            title="Save changes"
                          >
                            <SaveIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleCancelEdit(history, section)}
                            disabled={updatingHistory}
                            sx={{
                              color: '#6c757d',
                              '&:hover': {
                                backgroundColor: 'rgba(108, 117, 125, 0.1)',
                              },
                            }}
                            title="Cancel"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      ) : (
                        <Box display="flex" gap={0.5} justifyContent="center">
                          <IconButton
                            size="small"
                            onClick={() => handleEditHistory(history, section)}
                            sx={{
                              color: '#00798E',
                              '&:hover': {
                                backgroundColor: 'rgba(0, 121, 142, 0.1)',
                              },
                            }}
                            title="Edit record"
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteHistory(history, section)}
                            sx={{
                              color: '#dc3545',
                              '&:hover': {
                                backgroundColor: 'rgba(220, 53, 69, 0.1)',
                              },
                            }}
                            title="Delete record"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      );
    },
    [handleDeleteHistory, editingHistory, handleEditHistory, handleCancelEdit, handleSaveEdit, handleEditFieldChange, updatingHistory, hormonalOptions, serologyOptions, biochemistryOptions, semenAnalysisOptions, otherInvestigationOptions],
  );

  // Render form section with React Hook Form
  // Handle date change for any form section — prompts to fill empty date fields
  const handleDateChange = async (formType, index, date) => {
    setValue(`${formType}Forms.${index}.date`, date);
    const forms = getValues(`${formType}Forms`);
    const blanks = forms.filter((form, i) => i !== index && !form.date);
    if (blanks.length > 0) {
      const result = await Swal.fire({
        title: 'Apply Date to All?',
        text: `Do you want to apply this date to all ${blanks.length} empty date field(s)?`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#00798E',
        cancelButtonColor: '#6c757d',
        confirmButtonText: 'Yes, apply to all',
        cancelButtonText: 'No, keep empty',
      });
      if (result.isConfirmed) {
        forms.forEach((form, i) => {
          if (i !== index && !form.date) setValue(`${formType}Forms.${i}.date`, date);
        });
        await Swal.fire({ title: 'Date Applied', text: 'The date has been applied to all empty fields', icon: 'success', timer: 2000, showConfirmButton: false });
      }
    }
  };

  const renderFormSection = useCallback(
    (fields, appendFunction, removeFunction, options, title, saveFunction, formType, isSaving, draftObj, loadDraftHandler) => (
      <Card sx={{ mb: 3, boxShadow: '0 2px 4px rgba(0,0,0,0.1)', borderRadius: 2 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Box display="flex" alignItems="center" gap={2}>
              <Typography
                variant="h6"
                sx={{ color: '#00798E', fontWeight: 'bold', fontSize: '1.1rem' }}
              >
                {title}
              </Typography>
              {/* Draft Load/Clear Button */}
              <LoadDraftButton
                hasDraftData={draftObj?.hasDraftData}
                onLoadDraft={loadDraftHandler}
                onClearDraft={async () => await draftObj?.clearDraft()}
                timestamp={draftObj?.getFormattedTimestamp()}
              />
            </Box>
            <MDBox display="flex" justifyContent="center" gap={1}>
              <button
                type="button"
                onClick={appendFunction}
                disabled={isSaving}
                style={{
                  padding: '8px 16px',
                  background: 'transparent',
                  color: '#00798E',
                  border: '1px solid #00798E',
                  borderRadius: 4,
                  cursor: isSaving ? 'not-allowed' : 'pointer',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  opacity: isSaving ? 0.6 : 1,
                }}
              >
                <AddIcon sx={{ fontSize: '1rem' }} />
                Add Test
              </button>
            </MDBox>
          </Box>

          {/* Existing History Section */}
          <Box mb={3}>
            {loadingHistory ? (
              <Paper sx={{ p: 2, textAlign: 'center' }}>
                <Typography>Loading history...</Typography>
              </Paper>
            ) : (
              renderHistoryTable(existingHistory[formType], formType, title)
            )}
          </Box>

          <Divider sx={{ my: 2 }} />

          {fields.length === 0 ? (
            <Paper sx={{ p: 3, backgroundColor: '#f8f9fa', textAlign: 'center', borderRadius: 2 }}>
              <Typography color="text.secondary" sx={{ fontSize: '0.95rem' }}>
                No {title.toLowerCase()} tests added. Click &quot;Add Test&quot; to get started.
              </Typography>
              {/* eslint-disable-next-line react/prop-types */}
              {options.length === 0 && (
                <Typography color="error" variant="caption" display="block" sx={{ mt: 1 }}>
                  Note: No test options available. Please check investigation parameters.
                </Typography>
              )}
            </Paper>
          ) : (
            fields.map((field, index) => (
              <Paper
                key={field.id}
                sx={{
                  p: 3,
                  mb: 2,
                  backgroundColor: '#fafafa',
                  borderRadius: 2,
                  border: '1px solid #e0e0e0',
                }}
              >
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="subtitle2" sx={{ color: '#00798E', fontWeight: 'bold' }}>
                    Test #{index + 1}
                  </Typography>
                  <IconButton
                    onClick={() => removeFunction(index)}
                    size="small"
                    sx={{
                      color: '#dc3545',
                      '&:hover': {
                        backgroundColor: 'rgba(220, 53, 69, 0.1)',
                      },
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>

                <Grid container spacing={2}>
                  {/* Left side: Test Name, Date, and Value */}
                  <Grid item xs={12} md={formType === 'otherInvestigation' ? 5 : 7}>
                    <Grid container spacing={2} sx={formType === 'otherInvestigation' ? { alignItems: 'center', height: '100%' } : {}}>
                      {/* Test Name and Date in same row */}
                      <Grid item xs={12} md={formType === 'otherInvestigation' ? 7 : 6}>
                        {/* Conditionally render Test Name field - use text input for Other Investigation, dropdown for others */}
                        {formType === 'otherInvestigation' ? (
                          <ControlledTextField
                            name={`${formType}Forms.${index}.test_name`}
                            label="Test Name *"
                            required
                            control={control}
                            selectBoxHeight={selectBoxHeight}
                            placeholder="Enter other investigation test name"
                          />
                        ) : (
                          <ControlledSelect
                            name={`${formType}Forms.${index}.test_id`}
                            label="Test Name *"
                            options={options}
                            index={index}
                            control={control}
                            selectBoxHeight={selectBoxHeight}
                            onSelectionChange={(optionValue) => {
                              // Update test_name field using setValue
                              setValue(`${formType}Forms.${index}.test_name`, optionValue);
                            }}
                          />
                        )}
                      </Grid>

                      <Grid item xs={12} md={formType === 'otherInvestigation' ? 5 : 6}>
                        <MDDatePicker
                          label="Date *"
                          value={watch(`${formType}Forms.${index}.date`) || ''}
                          onChange={(date) => handleDateChange(formType, index, date)}
                          textFieldProps={{
                            fullWidth: true,
                            size: 'small',
                            required: true,
                            error: !!errors?.[`${formType}Forms`]?.[index]?.date,
                            helperText: errors?.[`${formType}Forms`]?.[index]?.date?.message,
                            sx: {
                              '& .MuiOutlinedInput-root': {
                                height: selectBoxHeight,
                                minHeight: selectBoxHeight,
                                '&.Mui-focused fieldset': {
                                  borderColor: '#00798E',
                                },
                              },
                            },
                          }}
                        />
                      </Grid>

                      {/* Value field below - exclude for Other Investigation */}
                      {formType !== 'otherInvestigation' && (
                        <Grid item xs={12}>
                          <Typography variant="body2" sx={{ mb: 0.5, color: '#666', fontSize: '0.875rem' }}>
                            Value
                          </Typography>
                          {/* Check if the selected test is ABORh in biochemistry */}
                          {formType === 'biochemistry' && 
                           watch(`${formType}Forms.${index}.test_name`)?.toLowerCase().includes('aborh') ? (
                            <FormControl fullWidth size="small" error={!!errors?.[`${formType}Forms`]?.[index]?.value}>
                              <InputLabel>Blood Group *</InputLabel>
                              <Select
                                value={watch(`${formType}Forms.${index}.value`) || ''}
                                onChange={(e) => setValue(`${formType}Forms.${index}.value`, e.target.value)}
                                label="Blood Group *"
                                required
                                sx={{
                                  height: selectBoxHeight,
                                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#00798E',
                                  },
                                }}
                              >
                                {BLOOD_GROUP_OPTIONS.map((option) => (
                                  <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                  </MenuItem>
                                ))}
                              </Select>
                              {errors?.[`${formType}Forms`]?.[index]?.value && (
                                <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                                  {errors[`${formType}Forms`][index].value.message}
                                </Typography>
                              )}
                            </FormControl>
                          ) : (
                            <RichTextEditor
                              value={watch(`${formType}Forms.${index}.value`) || ''}
                              onChange={(value) => setValue(`${formType}Forms.${index}.value`, value)}
                              height="60px"
                              placeholder="Enter result value"
                            />
                          )}
                        </Grid>
                      )}
                    </Grid>
                  </Grid>

                  {/* Right side: Remarks */}
                  <Grid item xs={12} md={formType === 'otherInvestigation' ? 7 : 5}>
                    <Typography variant="body2" sx={{ mb: 0.5, color: '#666', fontSize: '0.875rem' }}>
                      Remarks/Notes
                    </Typography>
                    <RichTextEditor
                      value={watch(`${formType}Forms.${index}.remarks`) || ''}
                      onChange={(value) => setValue(`${formType}Forms.${index}.remarks`, value)}
                      height="100px"
                      placeholder="Additional notes or observations"
                    />
                  </Grid>
                </Grid>
              </Paper>
            ))
          )}
          
          {/* Save Button at the bottom - More prominent */}
          {fields.length > 0 && (
            <Box display="flex" justifyContent="flex-end" mt={3}>
              <button
                type="button"
                onClick={saveFunction}
                disabled={isSaving}
                style={{
                  padding: '12px 32px',
                  background: '#00798E',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 6,
                  cursor: isSaving ? 'not-allowed' : 'pointer',
                  fontSize: '1rem',
                  fontWeight: '600',
                  opacity: isSaving ? 0.6 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  boxShadow: '0 2px 8px rgba(0, 121, 142, 0.3)',
                  transition: 'all 0.2s ease',
                }}
                onMouseOver={(e) => {
                  if (!isSaving) {
                    e.currentTarget.style.background = '#006675';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 121, 142, 0.4)';
                  }
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.background = '#00798E';
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 121, 142, 0.3)';
                }}
              >
                <SaveIcon sx={{ fontSize: '1.2rem' }} />
                Save {title}
              </button>
            </Box>
          )}
        </CardContent>
      </Card>
    ),
    [
      control,
      setValue,
      watch,
      errors,
      selectBoxHeight,
      existingHistory,
      loadingHistory,
      renderHistoryTable,
    ],
  );

  return (
    <Box sx={{ width: '100%', position: 'relative' }}>
      {/* Loader Overlays for different operations */}
      <LoaderOverlay open={loadingHistory} />
      <LoaderOverlay open={savingHormonal} />
      <LoaderOverlay open={savingSerology} />
      <LoaderOverlay open={savingBiochemistry} />
      <LoaderOverlay open={savingSemenAnalysis} />
      <LoaderOverlay open={savingOtherInvestigation} />
      <LoaderOverlay open={deletingHistory} />

      {/* Blood Investigation Accordion */}
      <Accordion
        expanded={expanded === 'blood'}
        onChange={handleChange('blood')}
        sx={{
          mb: 2,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          '&:before': { display: 'none' },
          borderRadius: '8px !important',
          overflow: 'hidden',
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            backgroundColor: '#f8f9fa',
            borderBottom: '1px solid #e9ecef',
            '& .MuiAccordionSummary-content': {
              alignItems: 'center',
              gap: 2,
            },
          }}
        >
          <BloodtypeIcon sx={{ color: '#dc3545' }} />
          <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#dc3545' }}>
            Blood Investigation
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 3 }}>
          {saveMessage && (
            <Alert
              severity={saveMessage.includes('Error') ? 'error' : 'success'}
              sx={{ mb: 3 }}
              onClose={() => setSaveMessage('')}
            >
              {saveMessage}
            </Alert>
          )}

          {/* HORMONAL Section */}
          {renderFormSection(
            hormonalFields,
            addHormonalForm,
            removeHormonal,
            hormonalOptions,
            'HORMONAL',
            saveHormonalData,
            'hormonal',
            savingHormonal,
            hormonalDraft,
            handleLoadHormonalDraft,
          )}

          <Divider sx={{ my: 3 }} />

          {/* SEROLOGY Section */}
          {renderFormSection(
            serologyFields,
            addSerologyForm,
            removeSerology,
            serologyOptions,
            'SEROLOGY',
            saveSerologyData,
            'serology',
            savingSerology,
            serologyDraft,
            handleLoadSerologyDraft,
          )}

          <Divider sx={{ my: 3 }} />

          {/* BIOCHEMISTRY Section */}
          {renderFormSection(
            biochemistryFields,
            addBiochemistryForm,
            removeBiochemistry,
            biochemistryOptions,
            'BIOCHEMISTRY',
            saveBiochemistryData,
            'biochemistry',
            savingBiochemistry,
            biochemistryDraft,
            handleLoadBiochemistryDraft,
          )}
        </AccordionDetails>
      </Accordion>

      {/* Semen Analysis Accordion */}
      <Accordion
        expanded={expanded === 'semen'}
        onChange={handleChange('semen')}
        sx={{
          mb: 2,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          '&:before': { display: 'none' },
          borderRadius: '8px !important',
          overflow: 'hidden',
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            backgroundColor: '#f8f9fa',
            borderBottom: '1px solid #e9ecef',
            '& .MuiAccordionSummary-content': {
              alignItems: 'center',
              gap: 2,
            },
          }}
        >
          <ScienceIcon sx={{ color: '#007bff' }} />
          <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#007bff' }}>
            Semen Analysis
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 3 }}>
          {saveMessage && (
            <Alert
              severity={saveMessage.includes('Error') ? 'error' : 'success'}
              sx={{ mb: 3 }}
              onClose={() => setSaveMessage('')}
            >
              {saveMessage}
            </Alert>
          )}

          {/* Semen Analysis Section */}
          {renderFormSection(
            semenAnalysisFields,
            addSemenAnalysisForm,
            removeSemenAnalysis,
            semenAnalysisOptions,
            'Semen Analysis',
            saveSemenAnalysisData,
            'semenAnalysis',
            savingSemenAnalysis,
            semenAnalysisDraft,
            handleLoadSemenAnalysisDraft,
          )}
        </AccordionDetails>
      </Accordion>

      {/* Other Investigation Accordion */}
      <Accordion
        expanded={expanded === 'otherInvestigation'}
        onChange={handleChange('otherInvestigation')}
        sx={{
          mb: 2,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          '&:before': { display: 'none' },
          borderRadius: '8px !important',
          overflow: 'hidden',
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            backgroundColor: '#f8f9fa',
            borderBottom: '1px solid #e9ecef',
            '& .MuiAccordionSummary-content': {
              alignItems: 'center',
              gap: 2,
            },
          }}
        >
          <LocalHospitalIcon sx={{ color: '#28a745' }} />
          <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#28a745' }}>
            Other Investigation
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 3 }}>
          {saveMessage && (
            <Alert
              severity={saveMessage.includes('Error') ? 'error' : 'success'}
              sx={{ mb: 3 }}
              onClose={() => setSaveMessage('')}
            >
              {saveMessage}
            </Alert>
          )}

          {/* Other Investigation Section */}
          {renderFormSection(
            otherInvestigationFields,
            addOtherInvestigationForm,
            removeOtherInvestigation,
            otherInvestigationOptions,
            'Other Investigation',
            saveOtherInvestigationData,
            'otherInvestigation',
            savingOtherInvestigation,
            otherInvestigationDraft,
            handleLoadOtherInvestigationDraft,
          )}
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}

MaleInvestigation.propTypes = {
  patientData: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    external_code: PropTypes.string,
    couple_code: PropTypes.string,
    gender: PropTypes.string,
    phone_number: PropTypes.string,
    email: PropTypes.string,
    occupation: PropTypes.string,
    nationality_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    nationality: PropTypes.string,
    marital_status: PropTypes.string,
    married_for: PropTypes.string,
    trying_for: PropTypes.string,
  }),
  investigationParams: PropTypes.shape({
    blood_investigation_params: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      param_group_members: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
          slug: PropTypes.string,
          param_group_member_options: PropTypes.arrayOf(
            PropTypes.shape({
              id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
              option_value: PropTypes.string,
            }),
          ),
        }),
      ),
    }),
    male_investigation_params: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      param_group_members: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
          slug: PropTypes.string,
          param_group_member_options: PropTypes.arrayOf(
            PropTypes.shape({
              id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
              option_value: PropTypes.string,
            }),
          ),
        }),
      ),
    }),
  }),
  onSaveInvestigation: PropTypes.func.isRequired,
  onFetchContactHistory: PropTypes.func,
  onDeleteHistory: PropTypes.func,
  saving: PropTypes.bool,
};

export default MaleInvestigation;
